import { FC, useEffect, useRef, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ReplyRoundedIcon from '@mui/icons-material/ReplyRounded';
import useVideoStore from '@/stores/videoStore';
import { useVideoCommentStore } from '@/stores/videoCommentStore';
import { formatTime } from '@/helpers/formatTime';
import CommentBody from './CommentBody';
import CommentComposer from './CommentComposer';
import { useCommentActions } from '../hooks/useCommentActions';
import { formatRelativeTime } from '../utils';
import { COMMENT_MAX_LENGTH } from '../config';
import type { Comment } from '../types';

interface CommentItemProps {
  comment: Comment;
  noteId: string;
  shareToken?: string | null;
  currentUserId?: string;
  isNoteOwner: boolean;
  isReply?: boolean;
}

const CommentItem: FC<CommentItemProps> = ({
  comment,
  noteId,
  shareToken,
  currentUserId,
  isNoteOwner,
  isReply = false,
}) => {
  const { setVideoCurrentTime, setProgressCurrentTime, setProgressTime } = useVideoStore();
  const highlightedCommentId = useVideoCommentStore((s) => s.highlightedCommentId);
  const setHighlightedCommentId = useVideoCommentStore((s) => s.setHighlightedCommentId);
  const { edit, remove, isSubmitting } = useCommentActions({ noteId, shareToken });

  const rootRef = useRef<HTMLDivElement | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editBody, setEditBody] = useState(comment.body);
  const [isReplying, setIsReplying] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const isAuthor = Boolean(currentUserId) && currentUserId === comment.author.id;
  const canEdit = isAuthor;
  const canDelete = isAuthor || isNoteOwner;
  const hasTimestamp = !isReply && comment.video_time !== null && comment.video_time >= 0;

  useEffect(() => {
    if (highlightedCommentId !== comment.id) return;
    const el = rootRef.current;
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    el.style.outline = '2px solid var(--mui-palette-primary-main)';
    const timer = setTimeout(() => {
      el.style.outline = '';
      setHighlightedCommentId(null);
    }, 2000);
    return () => clearTimeout(timer);
  }, [highlightedCommentId, comment.id, setHighlightedCommentId]);

  const handleSeek = () => {
    if (comment.video_time === null) return;
    setVideoCurrentTime(comment.video_time);
    setProgressCurrentTime(comment.video_time);
    setProgressTime();
    setHighlightedCommentId(comment.id);
  };

  const handleSaveEdit = async () => {
    const text = editBody.trim();
    if (!text) return;
    const result = await edit(comment.id, text);
    if (result) setIsEditing(false);
  };

  const handleDelete = async () => {
    const ok = await remove(comment.id);
    if (ok) setConfirmDelete(false);
  };

  return (
    <Box ref={rootRef} sx={{ borderRadius: 1, p: 0.5, transition: 'outline 0.2s' }}>
      <Stack direction="row" spacing={1.25} sx={{ width: '100%' }}>
        <Avatar
          src={comment.author.avatar ?? undefined}
          sx={{ width: isReply ? 28 : 36, height: isReply ? 28 : 36, flexShrink: 0 }}
        >
          {comment.author.name?.charAt(0)?.toUpperCase()}
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
            <Stack direction="row" alignItems="baseline" spacing={0.75} sx={{ minWidth: 0 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }} noWrap>
                {comment.author.name}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                {formatRelativeTime(comment.created_at)}
                {comment.edited ? ' · edited' : ''}
              </Typography>
            </Stack>
            {(canEdit || canDelete) && (
              <IconButton size="small" aria-label="Comment actions" onClick={(e) => setMenuAnchor(e.currentTarget)}>
                <MoreVertIcon fontSize="small" />
              </IconButton>
            )}
          </Stack>

          {hasTimestamp && (
            <Box
              component="button"
              onClick={handleSeek}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                background: 'none',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontFamily: 'inherit',
                fontSize: '11px',
                fontWeight: 600,
                letterSpacing: '0.02em',
                color: 'primary.main',
                px: 0,
                py: 0.25,
                mb: 0.25,
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              ▶ {formatTime(comment.video_time as number)}
            </Box>
          )}

          {isEditing ? (
            <Box sx={{ mt: 0.5 }}>
              <TextField
                value={editBody}
                onChange={(e) => setEditBody(e.target.value.slice(0, COMMENT_MAX_LENGTH))}
                multiline
                minRows={1}
                maxRows={6}
                fullWidth
                size="small"
                autoFocus
              />
              <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 0.75 }}>
                <Button
                  size="small"
                  color="inherit"
                  onClick={() => {
                    setEditBody(comment.body);
                    setIsEditing(false);
                  }}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  onClick={() => void handleSaveEdit()}
                  disabled={!editBody.trim() || isSubmitting}
                >
                  Save
                </Button>
              </Stack>
            </Box>
          ) : (
            <CommentBody body={comment.body} linkPreviews={comment.link_previews} />
          )}

          {!isReply && !isEditing && (
            <Button
              size="small"
              color="inherit"
              startIcon={<ReplyRoundedIcon fontSize="small" />}
              onClick={() => setIsReplying((prev) => !prev)}
              sx={{ mt: 0.25, textTransform: 'none', minWidth: 0 }}
            >
              Reply
            </Button>
          )}

          {!isReply && isReplying && (
            <Box sx={{ mt: 0.75 }}>
              <CommentComposer
                noteId={noteId}
                shareToken={shareToken}
                parentId={comment.id}
                placeholder="Write a reply…"
                submitLabel="Reply"
                autoFocus
                onSubmitted={() => setIsReplying(false)}
                onCancel={() => setIsReplying(false)}
              />
            </Box>
          )}

          {!isReply && comment.replies.length > 0 && (
            <Stack spacing={1} sx={{ mt: 1, pl: 1.5, borderLeft: '2px solid', borderColor: 'divider' }}>
              {comment.replies.map((reply) => (
                <CommentItem
                  key={reply.id}
                  comment={reply}
                  noteId={noteId}
                  shareToken={shareToken}
                  currentUserId={currentUserId}
                  isNoteOwner={isNoteOwner}
                  isReply
                />
              ))}
            </Stack>
          )}
        </Box>
      </Stack>

      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
        {canEdit && (
          <MenuItem
            onClick={() => {
              setEditBody(comment.body);
              setIsEditing(true);
              setMenuAnchor(null);
            }}
          >
            Edit
          </MenuItem>
        )}
        {canDelete && (
          <MenuItem
            onClick={() => {
              setConfirmDelete(true);
              setMenuAnchor(null);
            }}
          >
            Delete
          </MenuItem>
        )}
      </Menu>

      <Dialog open={confirmDelete} onClose={() => setConfirmDelete(false)}>
        <DialogTitle>Delete comment?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {isReply
              ? 'This reply will be permanently removed.'
              : 'This comment and any replies will be permanently removed.'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={() => setConfirmDelete(false)} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button color="error" variant="contained" onClick={() => void handleDelete()} disabled={isSubmitting}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CommentItem;
